import React, { useState, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Alert, Col as BSCol } from "components/bootstrap";
import { Row, Col } from "common/components";
import { useValidateSigmaRuleZip, useValidateSigmaRuleYaml } from "security-app/hooks/useSigmaAPI";
import { DEFAULT_RULE_OPTIONS } from "security-app/components/SigmaRules/SigmaEventDefinitions/utils";
import { FlexRow, FlexCol, StyledAlert } from "./styled-components";
import FileList from "./FileList";
import RuleFormWithTitle from "../RuleFormTitle";
const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./i;
const stripUuidPrefix = (name) => name.replace(UUID_PREFIX_RE, "");
function UploadRulesStep({ pathAction }) {
  const { t } = useTranslation("enterprise_security_sigma");
  const {
    values,
    errors: formErrors,
    setValues,
    setFieldTouched,
    setFieldError
  } = useFormikContext();
  const [files, setFiles] = useState(values == null ? void 0 : values.files);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const { validateSigmaRuleZip, isValidating: isValidatingZip } = useValidateSigmaRuleZip();
  const { validateSigmaRuleYaml, isValidating: isValidatingYaml } = useValidateSigmaRuleYaml();
  const isValidating = isValidatingZip || isValidatingYaml;
  useEffect(() => setSelectedFile(files == null ? void 0 : files[0]), [files, setSelectedFile]);
  useEffect(() => {
    setValues((prev) => ({ ...prev, files }));
    setFieldTouched("files", true, false);
  }, [files, setFieldTouched, setValues]);
  useEffect(() => {
    if (!files || files.length === 0) {
      setValidationErrors([]);
      setValues((prev) => ({ ...prev, files, invalid_file_names: [] }));
      return;
    }
    const zipFiles = files.filter((file) => file.name.endsWith(".zip"));
    const yamlFiles = files.filter((file) => !file.name.endsWith(".zip"));
    Promise.all([
      ...zipFiles.map((file) => validateSigmaRuleZip(file).then((response) => ({ sourceFile: file, response }))),
      ...yamlFiles.length > 0 ? [validateSigmaRuleYaml(yamlFiles).then((response) => ({ sourceFile: null, response }))] : []
    ]).then((results) => {
      const errors = [];
      const invalidNames = [];
      results.forEach(({ sourceFile, response }) => {
        const fileErrors = response.results.filter((result) => result.errors && result.errors.length > 0);
        errors.push(...fileErrors);
        if (fileErrors.length > 0) {
          if (sourceFile) {
            invalidNames.push(sourceFile.name);
          } else {
            invalidNames.push(...fileErrors.map((e) => e.file_name));
          }
        }
      });
      setValidationErrors(errors);
      setValues((prev) => prev.files === files ? { ...prev, invalid_file_names: invalidNames } : prev);
      setFieldError("files", errors.length > 0 ? "validation-failed" : void 0);
    });
  }, [files]);
  useEffect(() => {
    const ids = (files != null ? files : []).map((file) => file.name.split(".")[0]);
    setValues((prev) => {
      var _a;
      const current = (_a = prev.file_overrides) != null ? _a : {};
      const next = {};
      ids.forEach((id) => {
        var _a2;
        next[id] = (_a2 = current[id]) != null ? _a2 : DEFAULT_RULE_OPTIONS;
      });
      return { ...prev, files, file_overrides: next };
    });
  }, [files]);
  const selectedRuleId = useMemo(() => selectedFile == null ? void 0 : selectedFile.name.split(".")[0], [selectedFile]);
  const selectedFileName = useMemo(() => selectedFile == null ? void 0 : selectedFile.name.split(".").slice(1).join("."), [selectedFile]);
  const invalidFileNames = useMemo(() => {
    var _a;
    const invalid = new Set((_a = values.invalid_file_names) != null ? _a : []);
    (files != null ? files : []).forEach((file) => {
      var _a2;
      if ((_a2 = formErrors.file_overrides) == null ? void 0 : _a2[file.name.split(".")[0]]) invalid.add(file.name);
    });
    return invalid;
  }, [formErrors.file_overrides, files, values.invalid_file_names]);
  const isSelectedFileValidZip = !!(selectedFile == null ? void 0 : selectedFile.name.endsWith(".zip")) && !invalidFileNames.has(selectedFile.name);
  return /* @__PURE__ */ React.createElement(FlexRow, { style: { maxWidth: 2e3, margin: "0 auto" } }, /* @__PURE__ */ React.createElement(BSCol, { md: 5 }, /* @__PURE__ */ React.createElement(FileList, { files, setFiles, invalidFileNames, onNameClick: setSelectedFile }), !isValidating && validationErrors.length > 0 && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("upload_rules_step.validation_failed") }, /* @__PURE__ */ React.createElement(Col, null, validationErrors.map((err) => {
    var _a;
    return /* @__PURE__ */ React.createElement(Row, { key: err.file_name }, /* @__PURE__ */ React.createElement("strong", null, "\u2022"), /* @__PURE__ */ React.createElement("strong", null, stripUuidPrefix(err.file_name), ":"), /* @__PURE__ */ React.createElement("span", null, (_a = err.errors) == null ? void 0 : _a.join(", ")));
  })))), /* @__PURE__ */ React.createElement(FlexCol, { md: 7 }, /* @__PURE__ */ React.createElement(RuleFormWithTitle, { ruleId: selectedRuleId, ruleTitle: selectedFileName, pathAction }), isSelectedFileValidZip && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement("p", null, t("upload_rules_step.zip_shared_settings")))));
}
export default UploadRulesStep;
